import type { SessionRecord } from '@/hooks/mindspace/usePracticeHistory';
import {
  formatDuration,
  formatRowTime,
  scopeIcon,
  scopeLabel,
} from '@/components/features/mindspace/practice/utils';
import { BreakdownPills } from './BreakdownPills';

/**
 * One finished session in the history sidebar — ROUTE_INVENTORY §2.11 #19.
 *
 * Two renderings of the same record, and which one you get is a property of
 * the DATA, not of a prop: a session with any non-zero grade shows the
 * per-grade pills, and one with none falls back to a mastered percentage
 * toned at 70 / 40. The fallback exists for legacy records written before
 * the breakdown was captured, so both halves are reachable in production and
 * both are storied.
 */
export function HistoryRow({
  record,
  selected,
  onClick,
}: {
  record: SessionRecord;
  selected: boolean;
  onClick: () => void;
}) {
  // Colour notes, all measured on the panel surface (`bg-card/70` over
  // `--background`, which is a lavender rather than white):
  //   - the selected row's mode and timestamp were faded to /70 and /80 and
  //     measured 3.46:1 and 3.33:1. Selection is already carried by the fill.
  //   - the percentage fallback's -600 tones measured 4.12:1 (rose). The whole
  //     -600 family sits just under AA at this size on this surface.
  const Icon = scopeIcon(record.scope);

  return (
    <button
      type='button'
      onClick={onClick}
      className={`group flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors ${
        selected
          ? 'dark:tint-brand bg-brand-50 text-brand-900'
          : 'text-foreground hover:bg-muted'
      }`}
    >
      <Icon
        className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${
          selected
            ? 'text-brand-600 dark:text-primary'
            : 'text-muted-foreground'
        }`}
      />
      <div className='min-w-0 flex-1'>
        {/* Row 1 - scope + mode + exact time */}
        <div className='flex items-baseline justify-between gap-2'>
          <div className='flex min-w-0 items-baseline gap-1.5'>
            <span className='truncate text-xs font-medium'>
              {scopeLabel(record.scope)}
            </span>
            <span
              className={`flex-shrink-0 text-xs ${
                selected
                  ? 'text-brand-700 dark:text-primary'
                  : 'text-muted-foreground'
              }`}
            >
              {record.mode === 'flashcard' ? 'Flashcards' : 'Quiz'}
            </span>
          </div>
          <span
            className={`flex-shrink-0 text-xs tabular-nums ${
              selected
                ? 'text-brand-700 dark:text-primary'
                : 'text-muted-foreground'
            }`}
          >
            {formatRowTime(record.endedAt)}
          </span>
        </div>

        <RowMeta record={record} />
      </div>
    </button>
  );
}

/**
 * Row 2 — count, duration, and whichever summary the record supports.
 *
 * Its own component so the branch lives in one place: a record with any graded
 * outcome gets the pills, a legacy record with none gets the percentage, and a
 * session that recorded nothing gets neither. `0%` on an empty session would
 * be a number invented out of nothing.
 */
function RowMeta({ record }: { record: SessionRecord }) {
  const rated = record.mastered + record.improved + record.weak;
  const hasBreakdown = rated > 0;
  // An incomplete record's `total` is the deck it never finished, so the
  // count says how far it got: `1/3 concepts`, not `3 concepts`.
  const count = record.incomplete
    ? `${rated}/${record.total} concepts`
    : `${record.total} ${record.total === 1 ? 'concept' : 'concepts'}`;
  const masteredPct =
    record.total > 0 ? Math.round((record.mastered / record.total) * 100) : 0;

  return (
    <div className='mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground'>
      <span className='tabular-nums'>
        {count} · {formatDuration(record.durationMs)}
      </span>
      {hasBreakdown ? (
        <BreakdownPills
          mastered={record.mastered}
          improved={record.improved}
          weak={record.weak}
        />
      ) : record.total > 0 ? (
        <span
          className={`ml-auto font-semibold tabular-nums ${
            masteredPct >= 70
              ? 'text-emerald-700 dark:text-emerald-400'
              : masteredPct >= 40
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-rose-700 dark:text-rose-400'
          }`}
        >
          {masteredPct}%
        </span>
      ) : null}
    </div>
  );
}
